import React, { FC, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import { sprintf } from 'sprintf';

import * as Types from 'types';

import {
  encodeTokenID,
  getShortenedAddress,
  openSeaUrlForToken,
  etherscanUrlForTransaction,
} from 'services/nfts';

import { BigNumber } from 'ethers';
import { formatEther } from 'ethers/lib/utils';
import { useContract } from 'hooks/useContract';
import { useWagmi } from 'hooks/useWagmi';

import { StyledCheckbox } from 'components/UI/StyledCheckbox';
import { LinkWalletContainer } from 'components/LinkWallet/LinkWalletContainer';
import { LinkWalletModal } from 'components/LinkWallet/components/LinkWalletModal';
import { Spinner } from 'components/Spinner';
import { WalletCard } from 'components/UI/WalletCard';

import { MINTING_FEE_WEI, ISSUER_ADDRESS } from 'constants/global';
import { fetchEditions } from 'features/editions/actions';

import { NftEdition } from './components/NftEdition';
import { FeedbackSection } from './components/FeedbackSection';
import { MintingStateMessage } from './components/MintingStateMessage';
import { TableItem } from './components/TableItem';
import { ModalButtons } from './components/ModalButtons';

type Props = {
  edition: Types.EditionWithArtwork;
  showGiftEdition: (editionId: number) => void;
  onRequestClose: () => void;
};

export const EditionMintingFlow: FC<Props> = ({ edition, onRequestClose }) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLinkModalOpen, setLinkModalOpen] = useState(false);
  const [nftCreationRequested, setNftCreationRequested] = useState(false);

  const dispatch: Types.AppDispatch = useDispatch();

  const { verifiedAddress, isConnected } = useWagmi();
  const { txnRecipt, txnState, internalError, txnError, write, voucher } = useContract(
    edition.certificate?.transactionHash,
    edition.id,
  );
  const { current: initialTxnState } = useRef(txnState);

  const hash = edition.certificate?.transactionHash || txnRecipt?.hash;
  const tokenId = encodeTokenID(edition.artworkId, edition.editionNumber);
  const openSeaNftUrl = openSeaUrlForToken(tokenId.toString());
  const etherscanUrl = hash ? etherscanUrlForTransaction(hash) : undefined;

  const isCreateDisabled =
    !termsAccepted ||
    !verifiedAddress ||
    txnState === 'pending' ||
    txnState === 'loading' ||
    Boolean(internalError);
  const isMinted = txnState === 'success';
  const wasMintedOnMount = initialTxnState === 'success';

  const renderTransactionId = () => {
    return hash ? (
      <>
        <a
          target='_blank'
          rel='noopener noreferrer nofollow'
          className='link link--concealed'
          href={etherscanUrl}
          title='View on Etherscan'
        >
          {getShortenedAddress(hash, 'token')}
        </a>
      </>
    ) : txnState === 'pending' ? (
      <Spinner size='s' />
    ) : txnState === 'error' ? (
      'An error occurred'
    ) : (
      ''
    );
  };

  useEffect(() => {
    if (isMinted && !wasMintedOnMount) {
      dispatch(fetchEditions());
    }
  }, [isMinted]);

  useEffect(() => {
    if (nftCreationRequested && !isConnected) {
      setLinkModalOpen(true);
    }

    if (nftCreationRequested && isConnected && voucher) {
      write();
    }
  }, [nftCreationRequested, isConnected, voucher]);

  return (
    <div
      className={classnames(
        'minting-flow ',
        {
          'minting-flow--minted': isMinted,
        },
        'container-fluid px-0',
      )}
    >
      <div className='row no-gutters'>
        {/* Left side of modal, artwork display */}
        <section className='d-none d-md-block col-12 col-md-5 col-lg-4 m-0'>
          <NftEdition edition={edition} isMinted={isMinted} className={`py-md-3`} />
        </section>

        {/* Right side of modal, NFT creation */}
        <section className='minting-flow__details col-12 col-md-7 col-lg-8 pos-rel m-0 d-flex'>
          <div className='d-flex flex-column flex-grow-1'>
            <div className='minting-flow__details-top pt-2 pt-md-3 px-1 px-md-2 pr-lg-6'>
              <div className='minting-flow__headline-container mb-2 pb-1 pb-lg-0 mb-lg-0'>
                <MintingStateMessage txnState={txnState} initialTxnState={initialTxnState} />
              </div>
              <NftEdition
                edition={edition}
                isMinted={isMinted}
                className={`d-block d-md-none mb-${isMinted ? 3 : 2} mb-lg-0`}
                headlineEnabled={false}
              />
            </div>

            <div className='px-1 px-md-2 pr-lg-6 flex-grow-1'>
              <div className='table-items mb-1'>
                {!isMinted && (
                  <TableItem
                    title='Transfer from'
                    value={<WalletCard name='Sedition' address={ISSUER_ADDRESS} icon='logo' />}
                  />
                )}
                <TableItem
                  title={isMinted ? 'Owner' : 'Transfer to'}
                  value={<LinkWalletContainer />}
                />
                <TableItem
                  title='NFT Token ID'
                  value={
                    isMinted ? (
                      <a
                        className='link link--concealed'
                        target='_blank'
                        rel='noopener noreferrer nofollow'
                        href={openSeaNftUrl}
                      >
                        {getShortenedAddress(tokenId.toString(), 'token')}
                      </a>
                    ) : (
                      getShortenedAddress(tokenId.toString(), 'token')
                    )
                  }
                />
                {!isMinted && (
                  <TableItem
                    title='Sedition fee'
                    value={`${formatEther(BigNumber.from(MINTING_FEE_WEI))} ETH`}
                  />
                )}
                {hash && (
                  <>
                    <TableItem title='Transaction ID' value={renderTransactionId()} />
                    {isMinted && (
                      <TableItem
                        title='View on OpenSea'
                        value={
                          <a target='_blank' className='color-current' href={openSeaNftUrl}>
                            {edition.artwork.title}
                            <span className='icon icon-open mr-mini ml-mini' />
                          </a>
                        }
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            <div className='pb-2 pb-md-3 px-1 px-md-2 pr-lg-6'>
              <FeedbackSection
                txnState={txnState}
                initialTxnState={initialTxnState}
                txnError={txnError}
                etherscanUrl={etherscanUrl}
                internalError={internalError}
              />

              {!isMinted && txnState !== 'pending' && (
                <div className='mb-1'>
                  <StyledCheckbox
                    name=''
                    disabled={Boolean(internalError)}
                    label={
                      <span
                        dangerouslySetInnerHTML={{
                          __html: sprintf(
                            'I have read and understood the %sbenefits and the risks%s associated with transferring ownership information onto the Ethereum blockchain.',
                            `<a class="link-border" target="_blank" href="${'https://www.seditionart.com/help-centre/nfts/sedition-nfts-in-a-nutshell'}">`,
                            '</a>',
                          ),
                        }}
                      />
                    }
                    value=''
                    checked={termsAccepted}
                    onChange={setTermsAccepted}
                  />
                </div>
              )}
              <ModalButtons
                handleClose={onRequestClose}
                handleWrite={() => setNftCreationRequested(true)}
                isMinted={isMinted}
                isCreateDisabled={isCreateDisabled}
                txnState={txnState}
                showTooltip={!termsAccepted || !verifiedAddress}
              />
            </div>
          </div>
        </section>
      </div>
      <LinkWalletModal
        open={isLinkModalOpen}
        close={() => {
          setLinkModalOpen(false);
          setNftCreationRequested(false);
        }}
      />
    </div>
  );
};
